import { EntityType, useAppSelector } from '@frontend/common';
import { DeviceSensorClient } from '@frontend/device/api';
import { SensorLevel, SensorType } from '@frontend/device/types';
import { IoTClient } from '@frontend/iot/api';
import { IoTStateName } from '@frontend/iot/types';
import { PackageClient } from '@frontend/package/api';
import { Package } from '@frontend/package/types';
import { PinCode } from '@frontend/pin-code/types';
import { navigationStore, updateIoTState } from '@frontend/terminal/shared';
import { TransactionClient } from '@frontend/transaction/api';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { EnterPinProps } from './enter-pin.component';

interface ViewProps {
    state: PinWorkflowState;
    pinCodes: PinCode[];
    changePinCodes: React.Dispatch<React.SetStateAction<PinCode[]>>;
    _package: Package | null;
}

export enum PinWorkflowState {
    INIT = 'init',
    LOADING = 'loading',
    ENTER_PIN = 'enter_pin',
    ENTER_CHALLENGE = 'enter_challenge',
    DONE = 'done'
}
const useEnterPin = (props: EnterPinProps): ViewProps => {
    const iot = useAppSelector(useSelector, navigationStore).iot;
    const device = useAppSelector(useSelector, navigationStore).device;
    const [pinCodes, changePinCodes] = useState<PinCode[]>([]);
    const [state, changeState] = useState<PinWorkflowState>(PinWorkflowState.INIT);
    const [_package, changePackage] = useState<Package | null>(null);

    useEffect(() => {
        changeState(PinWorkflowState.ENTER_PIN);
        if (iot) {
            IoTClient.patchIoT(iot.account_id, iot.id, { state: IoTStateName.IOT_PICK_UP_PACKAGE }).then((iot) => {
                props.dispatch(updateIoTState({ iot_state: iot.state }));
            });
        }
    }, []);

    useEffect(() => {
        if (iot && device && pinCodes && pinCodes.length > 0) {
            const pin = pinCodes[0];
            if (pinCodes.length > 1) {
                changeState(PinWorkflowState.ENTER_CHALLENGE);
            } else {
                DeviceSensorClient.postDeviceSensor(iot.account_id, iot.id, device.id, {
                    level: SensorLevel.INFO,
                    data: { input: pin.code },
                    type: SensorType.BADGE_NUMBER
                });

                if (pin.entity_type === EntityType.PACKAGE) {
                    PackageClient.resolvePackages([pin.entity_id]).then((res) => {
                        if (res.results.length > 0) {
                            changePackage(res.results[0]);
                        }
                    });
                } else if (pin.entity_type === EntityType.TRANSACTION) {
                    TransactionClient.fetchTransaction(pin.account_id, pin.entity_id).then((transaction) => {
                        PackageClient.fetchPackages({ account_id: pin.account_id, transaction_id: transaction.id }).then((packages) => {
                            const pendingPackage = packages.results.find((p) => p.state.includes('pending'));
                            if (pendingPackage) {
                                changePackage(pendingPackage);
                            } else {
                                changePackage(null);
                            }
                        });
                    });
                }
                changeState(PinWorkflowState.DONE);
            }
        }
    }, [pinCodes]);

    return {
        state,
        pinCodes,
        changePinCodes,
        _package
    };
};

export default useEnterPin;
